import { forwardRef, MouseEventHandler } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { MenuListItems } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { IconButton, Popover, Tooltip } from '@lyearn/ui';
import { ToolbarButtonProps } from '@/components/Editor/PlainEditor/types';
import { useGenerativeTaskContext } from '@/components/LyearnAI/context/generativeTaskContext';

import { useAIEditorContext } from '../../../../context/AIEditorContext';
import useAIEditorMenu from '../../../../hooks/useAIEditorMenu';
import { AIEditorState } from '../../../../types';
import StopGeneratingIcon from './StopGeneratingIcon';
import { aiToolbarBtn } from './styles';

type AIButtonProps = {
  title: string | undefined;
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  Icon: ToolbarButtonProps['Icon'];
  hideTooltip: boolean;
  className?: string;
};

const AIButton = forwardRef<unknown, AIButtonProps>(
  ({ title, onClick, Icon, hideTooltip, className }, ref) => (
    <Tooltip
      ref={ref}
      classes={{ popper: cn({ hidden: hideTooltip }) }}
      placement="bottom"
      title={title}>
      <IconButton
        Icon={Icon}
        appearance="secondary"
        className={cn(aiToolbarBtn, className)}
        shape="square"
        size="s"
        variant="ghost"
        onClick={onClick}
      />
    </Tooltip>
  ),
);

AIButton.displayName = 'AIButton';

const AddAIContent: React.FC<ToolbarButtonProps> = ({
  editorView,
  tooltipKey,
  Icon,
  className,
  classes,
}) => {
  const { t } = useTranslation('AI');
  const { isCancellable } = useGenerativeTaskContext();
  const { editorState } = useAIEditorContext();
  const { menuItems, onEmptyMenuClick, isPopoverVisible, onPopoverOpenChange, onMenuItemAction } =
    useAIEditorMenu({ editorView });

  const tooltipTitle = tooltipKey ? t(tooltipKey)! : '';

  if (isCancellable) {
    return <StopGeneratingIcon className={cn(aiToolbarBtn, className)} />;
  }

  if (editorState === AIEditorState.Empty) {
    return (
      <AIButton
        Icon={Icon}
        className={className}
        hideTooltip={false}
        title={tooltipTitle}
        onClick={onEmptyMenuClick}
      />
    );
  }

  return (
    <Popover open={isPopoverVisible} onOpenChange={onPopoverOpenChange}>
      <Popover.Button>
        <AIButton
          Icon={Icon}
          className={className}
          hideTooltip={isPopoverVisible}
          title={tooltipTitle}
        />
      </Popover.Button>
      <Popover.Content
        className={cn('z-[9999] h-[24.8rem] overflow-auto p-12 pt-16', classes?.popoverContent)}
        minWidth={23.2}
        position="bottom-start"
        renderAs="popper">
        <MenuListItems
          className="space-y-4 p-0 xs:mb-12 xs:px-8 xs:pb-32"
          items={menuItems}
          onAction={onMenuItemAction}
        />
      </Popover.Content>
    </Popover>
  );
};

export default AddAIContent;
